import { Autocomplete } from '@base-ui/react/autocomplete';
import { useNavigate } from '@tanstack/react-router';
import { cn } from 'cn';
import { Loader, MapPin } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { useAmpli, useAmpliEffectOnce } from '@/ampli';
import type { CompareAddressLookupFailedProperties } from '@/ampli/generated';
import useCompareAddressAutocomplete from '@/hooks/use-compare-address-autocomplete';
import useCompareAddressCreateFailure from '@/hooks/use-compare-address-create-failure';
import useCompareCreateAreaPreview from '@/hooks/use-compare-create-area-preview';
import useStartCreateOnboardingDraft from '@/hooks/use-start-create-onboarding-draft';
import { tv } from '@/lib/tv';

const compareAddressFinderStyles = tv({
  slots: {
    label: 'text-[13px] font-medium text-ink-soft',
    hint: 'm-0 text-sm text-danger',
  },
  variants: {
    tone: {
      default: {},
      inverse: {
        label: 'sr-only',
        hint: 'text-danger-inverse',
      },
    },
  },
  defaultVariants: {
    tone: 'default',
  },
});

type CompareAddressFinderProps = {
  placement: CompareAddressLookupFailedProperties['placement'];
  blogSlug?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  className?: string;
  tone?: 'default' | 'inverse';
  addressPlaceholder?: string;
};

type AddressSuggestion = ReturnType<
  typeof useCompareAddressAutocomplete
>['suggestions'][number];

/**
 * Address autocomplete that creates an area preview and an onboarding draft
 * for it, then opens the start flow. Shared by the start page and the
 * embeddable `CompareWidget`, `CompareSection`, and the directory branch hero.
 */
const CompareAddressFinder = ({
  placement,
  blogSlug,
  submitLabel = 'Compare local agents',
  autoFocus,
  className,
  tone = 'default',
  addressPlaceholder = 'Start typing, e.g. 12 Acacia Avenue',
}: CompareAddressFinderProps) => {
  const styles = compareAddressFinderStyles({ tone });
  const navigate = useNavigate();
  const ampli = useAmpli();
  const autocomplete = useCompareAddressAutocomplete({ placement, blogSlug });
  const areaPreview = useCompareCreateAreaPreview();
  const onboardingDraft = useStartCreateOnboardingDraft();
  const isCreating = areaPreview.isCreating || onboardingDraft.isCreating;
  const [query, setQuery] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const hasTrackedSubmitWithoutSelectionRef = useRef(false);
  const inputId = useId();
  const hintId = useId();
  const status = getStatusMessage({
    query,
    minQueryLength: autocomplete.minQueryLength,
    isSearching: autocomplete.isSearching,
    hasError: autocomplete.hasError,
    suggestionCount: autocomplete.suggestions.length,
  });
  useAmpliEffectOnce((client) => {
    client.compareAddressFinderViewed({ placement, blog_slug: blogSlug });
  });
  useCompareAddressCreateFailure({
    blogSlug,
    failure: areaPreview.failure,
    placement,
  });
  const createHint =
    areaPreview.failure == null
      ? null
      : 'We couldn’t look up that address. Please try again.';
  const visibleHint = hint ?? createHint;

  const handleQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);
    setHint(null);
    autocomplete.search(nextQuery);
  };

  const handleSelect = async (suggestion: AddressSuggestion) => {
    if (isCreating) {
      return;
    }

    ampli((client) =>
      client.compareAddressSelected({ placement, blog_slug: blogSlug }),
    );
    const previewId = await areaPreview.createPreview(suggestion.id);

    if (previewId == null) {
      return;
    }

    try {
      const draftId = await onboardingDraft.createDraft(previewId);

      if (draftId == null) {
        setHint('We couldn’t look up that address. Please try again.');
        return;
      }

      await navigate({
        to: '/start',
        search: { draftId, step: 1 },
      });
    } catch (error) {
      console.error(error);
      setHint('We couldn’t look up that address. Please try again.');
    }
  };

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasTrackedSubmitWithoutSelectionRef.current) {
      hasTrackedSubmitWithoutSelectionRef.current = true;
      ampli((client) =>
        client.compareAddressLookupFailed({
          placement,
          blog_slug: blogSlug,
          reason: 'submitted_without_selection',
        }),
      );
    }
    setHint(
      query.trim().length === 0
        ? 'Start typing your property address.'
        : 'Pick your address from the suggestions.',
    );
  };

  return (
    <form
      className={cn('@container flex flex-col gap-2', className)}
      noValidate
      onSubmit={handleSubmit}
    >
      {areaPreview.verificationWidget}
      {onboardingDraft.verificationWidget}
      <label className={styles.label()} htmlFor={inputId}>
        Property address
      </label>
      <div className="flex flex-col gap-2.5 @min-[480px]:flex-row">
        <Autocomplete.Root
          autoHighlight
          filter={null}
          itemToStringValue={(suggestion: AddressSuggestion) =>
            suggestion.label
          }
          items={autocomplete.suggestions}
          onValueChange={(nextQuery, { reason }) => {
            if (reason !== 'item-press') {
              handleQueryChange(nextQuery);
            }
          }}
          value={query}
        >
          <Autocomplete.InputGroup className="relative flex h-14 min-w-0 flex-none items-center rounded-xl border border-line-field bg-field transition-[border-color,box-shadow,background-color] duration-150 ease-out focus-within:border-ink focus-within:bg-white focus-within:shadow-field hover:border-line-strong motion-reduce:transition-none @min-[480px]:flex-1">
            <MapPin
              aria-hidden="true"
              className="pointer-events-none absolute left-4 size-5 text-ink-muted"
            />
            <Autocomplete.Input
              aria-describedby={visibleHint ? hintId : undefined}
              aria-invalid={visibleHint ? true : undefined}
              autoComplete="off"
              autoFocus={autoFocus}
              className="h-full w-full min-w-0 rounded-xl bg-transparent pr-11 pl-12 text-base text-ink outline-none placeholder:text-ink-muted disabled:opacity-70"
              disabled={isCreating}
              id={inputId}
              placeholder={addressPlaceholder}
            />
            {autocomplete.isSearching || isCreating ? (
              <Loader
                aria-hidden="true"
                className="pointer-events-none absolute right-4 size-4 animate-spin text-ink-muted motion-reduce:animate-none"
              />
            ) : null}
          </Autocomplete.InputGroup>
          <Autocomplete.Portal
            hidden={status === null && autocomplete.suggestions.length === 0}
          >
            <Autocomplete.Positioner
              align="start"
              className="z-50 outline-none"
              sideOffset={8}
            >
              <Autocomplete.Popup
                aria-busy={autocomplete.isSearching || undefined}
                className="surface-card w-[var(--anchor-width)] max-w-[var(--available-width)] origin-[var(--transform-origin)] overflow-hidden p-1.5 transition-[opacity,transform] duration-150 ease-out data-ending-style:scale-[0.98] data-ending-style:opacity-0 data-starting-style:scale-[0.98] data-starting-style:opacity-0 motion-reduce:transition-none"
              >
                <Autocomplete.Status className="px-3 text-[13px] text-ink-muted empty:hidden">
                  {status ? <p className="m-0 py-2.5">{status}</p> : null}
                </Autocomplete.Status>
                <Autocomplete.List className="flex max-h-[min(var(--available-height),20rem)] flex-col overflow-y-auto overscroll-contain">
                  {(suggestion: AddressSuggestion) => (
                    <Autocomplete.Item
                      className="flex cursor-pointer items-start gap-3 rounded-[12px] px-3 py-2.5 text-[15px] leading-snug text-ink select-none data-highlighted:bg-accent-tint"
                      key={suggestion.id}
                      onClick={() => void handleSelect(suggestion)}
                      value={suggestion}
                    >
                      <MapPin
                        aria-hidden="true"
                        className="mt-0.5 size-4 flex-none text-ink-muted"
                      />
                      {suggestion.label}
                    </Autocomplete.Item>
                  )}
                </Autocomplete.List>
              </Autocomplete.Popup>
            </Autocomplete.Positioner>
          </Autocomplete.Portal>
        </Autocomplete.Root>
        <button
          className="button-primary h-14 flex-none px-6 text-base transition-[background-color,transform] duration-150 ease-out hover:bg-accent/85 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-70 motion-reduce:transition-none"
          disabled={isCreating}
          type="submit"
        >
          {isCreating ? 'Finding your address…' : submitLabel}
        </button>
      </div>
      {visibleHint ? (
        <p className={styles.hint()} id={hintId} role="alert">
          {visibleHint}
        </p>
      ) : null}
    </form>
  );
};

const getStatusMessage = ({
  query,
  minQueryLength,
  isSearching,
  hasError,
  suggestionCount,
}: {
  query: string;
  minQueryLength: number;
  isSearching: boolean;
  hasError: boolean;
  suggestionCount: number;
}): string | null => {
  if (hasError) {
    return 'We couldn’t look up addresses just now. Please try again.';
  }

  if (query.trim().length < minQueryLength) {
    return null;
  }

  if (isSearching && suggestionCount === 0) {
    return 'Searching addresses…';
  }

  if (!isSearching && suggestionCount === 0) {
    return 'No matches yet. Try your house number and street, or the postcode.';
  }

  return null;
};

export default CompareAddressFinder;
